/**
 * Does the pantry cover this ingredient? Asked once, answered once.
 *
 * Every screen that shows a have/buy decision calls THIS. Before it existed,
 * `ingredientStatusService` and `calculateRequiredQuantities` each ran their own
 * matcher and reached opposite conclusions about the same line — the recipe page
 * said you had "2 tbsp lime juice" while the grocery list told you to buy it.
 * Two functions cannot disagree if there is only one.
 *
 * The three states are deliberate. `probably` is not a hedge, it is the honest
 * answer to a genuinely uncertain match, and it renders as a question the user
 * can settle in one tap. Collapsing it into `have` sends someone to the stove
 * without an ingredient; collapsing it into `buy` sends them to the shop for
 * something already in the fridge. Both were happening.
 */

import {
  resolveIngredient,
  acceptableIds,
  requiredIds,
  type IngredientRef,
} from '../utils/resolveIngredient';
import { foldSurface, type CanonicalIngredientId } from '../utils/ingredientVocabulary';
import { stapleKeysForLine, isTapWater, assumedOnHand } from '../constants/staples';
import { cleanPantryName } from '../utils/pantryFoodName';
import { yieldCoverage, type YieldAnswer } from '../utils/ingredientYield';
import { relationsTo, type RelationType } from '../data/ingredientRelations';
import { isCookedLeftover } from '../utils/pantryLocation';

export type CoverageState = 'have' | 'probably' | 'buy' | 'staple';

export interface CoverageSource {
  /** Id of the pantry row that covers it, when one does. */
  itemId: string;
  /** What the pantry row is called, for the row label. */
  itemName: string;
  /** Why it counts. */
  relation: 'same' | 'alternative' | RelationType;
}

/** Round for a human: 5, 2.5, never 2.4999999. */
const tidy = (value: number): number => Math.round(value * 10) / 10;

/**
 * What a `probably` row needs to ask its question — as DATA, not a sentence.
 *
 * This used to be a pre-built string, which meant an English "— need 2" and an
 * English pluralised unit rendered inside the French UI. The service does not
 * know the user's language and has no business writing prose; it reports the
 * numbers and the caller writes the sentence through i18n.
 */
export interface CoverageDetail {
  /** The pantry row, as the user named it. */
  itemName: string;
  /** How many of it the pantry holds, when the row says. */
  itemQuantity?: number;
  /** What it yields, already converted into the recipe's own unit. */
  available?: number;
  /** What the recipe asks for. */
  needed?: number;
  /** The recipe's unit, verbatim — already in the recipe's language. */
  unit?: string;
}

function detailFor(item: CoverageItem, answer: YieldAnswer): CoverageDetail {
  return {
    itemName: item.name,
    ...(item.quantity && item.quantity > 1 ? { itemQuantity: item.quantity } : {}),
    ...(answer.available !== null ? { available: tidy(answer.available) } : {}),
    ...(answer.needed !== null ? { needed: tidy(answer.needed) } : {}),
    ...(answer.unit ? { unit: answer.unit } : {}),
  };
}

export interface CoverageResult {
  state: CoverageState;
  /** The pantry rows that cover this line. Empty for `buy` and `staple`. */
  via: CoverageSource[];
  confidence: number;
  /** Numbers behind a `probably`, for the caller to phrase. Absent otherwise. */
  detail?: CoverageDetail;
  /** The resolved line, so callers do not resolve it a second time. */
  ref: IngredientRef;
}

/** Minimal shape this service needs from a pantry row. */
export interface CoverageItem {
  id: string;
  name: string;
  /** Present once the pantry stores its resolved identity. */
  canonicalId?: string | null;
  status?: string;
  quantity?: number;
  unit?: string;
  /** Only the cook-capture flow writes 'leftover'. See `isCookedLeftover`. */
  source?: string;
}

/**
 * What the user has already answered, so a question is asked once.
 *
 * Directed: `covers` holds "having A covers B", which is not the same claim as
 * "A and B are the same food" and must not be read in either direction.
 */
export interface LearnedCoverage {
  covers: Array<{ a: string; b: string }>;
  notCovers: Array<{ a: string; b: string }>;
}

/** A pantry reduced to canonical ids, built once per screen. */
export interface PantryIndex {
  byId: Map<CanonicalIngredientId, CoverageItem[]>;
  /** Answers the user has given. Empty when nothing has been taught yet. */
  learned: LearnedCoverage;
  /**
   * Exact reduced text, for foods the vocabulary has never heard of.
   *
   * The vocabulary will always be smaller than the world, and #601 promises
   * that whatever « Je l'ai » wrote reads back as have. "3 panais, pelés et
   * râpés" resolves to nothing, so without this the parsnip you just shelved
   * comes straight back as buy. Exact equality on the reduced string only —
   * this is a round-trip guarantee, not a second matcher.
   */
  byText: Map<string, CoverageItem[]>;
  items: CoverageItem[];
  /**
   * Staple groups the cook turned off in Basiques. Such a staple is an ordinary
   * ingredient here, exactly as on the grocery list: covered only by the pantry.
   */
  excludedStaples: readonly string[];
}

/** The reduced form both the pantry write path and this read path agree on. */
function textKey(name: string): string {
  const cleaned = cleanPantryName(name);
  return foldSurface(cleaned.usable ? cleaned.name : name);
}

/**
 * The key and its naive singular/plural twin.
 *
 * A pantry row is written from what the user shelved ("topinambour") while the
 * recipe line names several ("3 topinambours, pelés et râpés"). One is not the
 * other as a string, and for a food the vocabulary does not know there is
 * nothing else to fall back on. Still exact matching — just not defeated by a
 * trailing s.
 */
function textKeys(name: string): string[] {
  const key = textKey(name);
  if (!key) return [];
  const twin = key.endsWith('s') ? key.slice(0, -1) : `${key}s`;
  return twin.length > 2 ? [key, twin] : [key];
}

/**
 * Resolve the pantry once, not once per ingredient.
 *
 * `canonicalId` is read from the row when present — the pantry stores it at
 * write time so the key that was saved and the key being matched are the same
 * value rather than two normalizers that agree by luck (#601). Rows written
 * before that resolve here instead, so no migration is needed to ship.
 */
export function buildPantryIndex(
  items: CoverageItem[],
  learned: LearnedCoverage = { covers: [], notCovers: [] },
  excludedStaples: readonly string[] = []
): PantryIndex {
  const byId = new Map<CanonicalIngredientId, CoverageItem[]>();
  const byText = new Map<string, CoverageItem[]>();
  /*
   * A dish the user cooked is not an ingredient (#663) and must never cover a
   * recipe line or cancel a shopping one.
   *
   * The guard lives HERE, not in each caller, and that is the whole point of
   * this service. It was added to `inventoryMatchService` and
   * `calculateRequiredQuantities` separately; when the recipe screen stopped
   * going through `getInventoryMatchesForRecipe`, the first of those became
   * dead code and the two screens silently disagreed again about leftovers.
   * One index, one guard, no way to bypass it.
   */
  const active = items.filter(
    item => (!item.status || item.status === 'active') && !isCookedLeftover(item)
  );

  const push = <K>(map: Map<K, CoverageItem[]>, key: K, item: CoverageItem) => {
    const bucket = map.get(key);
    if (bucket) bucket.push(item);
    else map.set(key, [item]);
  };

  for (const item of active) {
    const id = item.canonicalId ?? resolveIngredient(item.name).canonicalId;
    if (id) push(byId, id, item);

    for (const key of textKeys(item.name)) push(byText, key, item);
  }

  return { byId, byText, learned, items: active, excludedStaples };
}

function firstMatch(index: PantryIndex, id: CanonicalIngredientId): CoverageItem | null {
  return index.byId.get(id)?.[0] ?? null;
}

/**
 * Coverage for one already-resolved line.
 *
 * Order matters and is not arbitrary: an exact match beats an alternative,
 * which beats owning the thing it is made from. The first two are certain, the
 * third is the one worth asking about.
 */
export function coverageFor(
  ref: IngredientRef,
  index: PantryIndex,
  /**
   * The whole line, when the caller has it. A compound or "or" line's `ref.raw`
   * is only its first part, and the staple decision has to read the line the
   * grocery list reads, or the two screens disagree again (#796).
   */
  line: string = ref.raw
): CoverageResult {
  if (!ref.canonicalId) {
    return (
      byExactText(ref) ??
      assumed(ref, line, ref.unit, index) ?? { ref, via: [], state: 'buy', confidence: 0 }
    );
  }

  function byExactText(unresolved: IngredientRef): CoverageResult | null {
    const item = index.byText.get(textKey(unresolved.raw))?.[0];
    if (!item) return null;
    return {
      ref: unresolved,
      state: 'have',
      via: [{ itemId: item.id, itemName: item.name, relation: 'same' }],
      confidence: 1
    };
  }

  // "Sel et poivre" needs BOTH. Report the weakest part, so a line is only
  // covered when everything it names is.
  const required = requiredIds(ref);
  if (required.length > 1) {
    const parts = required.map(id => {
      const part = id === ref.canonicalId ? ref : ref.components.find(c => c.canonicalId === id);
      return coverageForId(id, index, ref, part?.raw ?? '', part?.unit ?? ref.unit);
    });
    const worst = parts.reduce((a, b) => (rank(a.state) <= rank(b.state) ? a : b));
    return {
      ref,
      state: worst.state,
      via: parts.flatMap(part => part.via),
      confidence: Math.min(...parts.map(part => part.confidence)),
      detail: worst.detail,
    };
  }

  return coverageForId(ref.canonicalId, index, ref, line, ref.unit);
}

/**
 * A line nobody has to stock: tap water, or a staple the cook has not turned
 * off. Asked of the same text, with the same function, as the grocery list's
 * "already have" — that is the whole fix for #796.
 */
function assumed(
  ref: IngredientRef,
  text: string,
  unit: string | undefined,
  index: PantryIndex
): CoverageResult | null {
  if (isTapWater(text) || assumedOnHand(stapleKeysForLine(text, unit), index.excludedStaples)) {
    return { ref, state: 'staple', via: [], confidence: ref.confidence };
  }
  return null;
}

const rank = (state: CoverageState): number =>
  state === 'have' ? 3 : state === 'staple' ? 2 : state === 'probably' ? 1 : 0;

function coverageForId(
  id: CanonicalIngredientId,
  index: PantryIndex,
  ref: IngredientRef,
  /** The text naming this id — the whole line, or this part of a compound. */
  text: string,
  unit: string | undefined
): CoverageResult {
  // Exactly this food.
  const exact = firstMatch(index, id);
  if (exact) {
    return {
      ref,
      state: 'have',
      via: [{ itemId: exact.id, itemName: exact.name, relation: 'same' }],
      confidence: ref.confidence
    };
  }

  // Something the line explicitly said was acceptable instead.
  for (const alternativeId of acceptableIds(ref)) {
    if (alternativeId === id) continue;
    const item = firstMatch(index, alternativeId);
    if (!item) continue;
    return {
      ref,
      state: 'have',
      via: [{ itemId: item.id, itemName: item.name, relation: 'alternative' }],
      confidence: ref.confidence
    };
  }

  // Something the pantry holds that this can be made from, is part of, or is a
  // cut of. This is where "5 limes" finally gets to pay for "2 tbsp lime juice".
  for (const relation of relationsTo(id)) {
    const item = firstMatch(index, relation.from);
    if (!item) continue;

    // The user has already answered this exact question. Their answer wins over
    // anything computed — it is their kitchen, and asking twice teaches them
    // that answering does not stick.
    if (index.learned.notCovers.some(p => p.a === relation.from && p.b === id)) continue;
    if (index.learned.covers.some(p => p.a === relation.from && p.b === id)) {
      return {
        ref,
        state: 'have',
        via: [{ itemId: item.id, itemName: item.name, relation: relation.type }],
        confidence: 1
      };
    }

    const answer = yieldCoverage(relation.from, item.quantity, id, ref.quantity, ref.unit);

    // Arithmetic said yes. This is the only path that upgrades a related
    // ingredient to a plain `have` — everything else asks.
    if (answer.verdict === 'enough') {
      return {
        ref,
        state: 'have',
        via: [{ itemId: item.id, itemName: item.name, relation: relation.type }],
        confidence: 0.85,
        detail: detailFor(item, answer),
      };
    }

    // Arithmetic said no. The pantry helps but does not cover it, so the row
    // stays on the list rather than quietly shrinking.
    if (answer.verdict === 'short') continue;

    return {
      ref,
      state: 'probably',
      via: [{ itemId: item.id, itemName: item.name, relation: relation.type }],
      confidence: 0.6,
      detail: detailFor(item, answer),
    };
  }

  return assumed(ref, text, unit, index) ?? { ref, state: 'buy', via: [], confidence: ref.confidence };
}

/** Resolve and answer in one call, for a caller holding raw text. */
export function coverageForLine(line: string, index: PantryIndex): CoverageResult {
  return coverageFor(resolveIngredient(line), index, line);
}
